import React, { useEffect, useRef, useState } from 'react';
import Slider from 'rsuite/Slider';
import { ajustarPagoInicial, mensajeAjuste, parseMonto, pctPagoInicial } from '../../../../../Utils/pagoInicial';

const AVISO_AJUSTE_MS = 4000;

/* Bloque "Enganche": barra de % + monto editable a mano.
 * El usuario solo escribe el monto; el % se deriva. Si el monto queda fuera de
 * [minPct, maxPct] sobre vehicleTotal, se ajusta al límite y se avisa ~4 s. */
function EngancheBlock({
    enganche,
    vehicleTotal,
    totalIsReady,
    sliderPct,
    pctLabel,
    minPct,
    maxPct,
    formatMoney,
    onDownPaymentChange,
    onDownPaymentAmountChange,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [aviso, setAviso] = useState(null);
    const inputRef = useRef(null);
    // Evita aplicar dos veces (Enter + blur) o aplicar después de Esc.
    const editActivoRef = useRef(false);
    const puedeEditar = totalIsReady && typeof onDownPaymentAmountChange === 'function';

    useEffect(() => {
        if (!editing || !inputRef.current) return;
        inputRef.current.focus();
        inputRef.current.select();
    }, [editing]);

    useEffect(() => {
        if (!aviso) return undefined;
        const timer = setTimeout(() => setAviso(null), AVISO_AJUSTE_MS);
        return () => clearTimeout(timer);
    }, [aviso]);

    const empezarEdicion = () => {
        if (!puedeEditar) return;
        editActivoRef.current = true;
        setDraft(String(Math.round(Number(enganche) || 0)));
        setEditing(true);
    };

    const cancelarEdicion = () => {
        editActivoRef.current = false;
        setEditing(false);
    };

    const aplicarEdicion = () => {
        if (!editActivoRef.current) return;
        editActivoRef.current = false;
        setEditing(false);
        const parsed = parseMonto(draft);
        if (parsed === null) return;
        const resultado = ajustarPagoInicial({ monto: parsed, base: vehicleTotal, minPct, maxPct });
        onDownPaymentAmountChange(resultado.monto);
        setAviso(resultado.ajuste
            // Objeto nuevo en cada ajuste para reiniciar el timer aunque el texto se repita.
            ? { texto: mensajeAjuste(resultado.ajuste, resultado, { minPct, maxPct }, formatMoney) }
            : null);
    };

    // Mientras se escribe, el % sigue al borrador (sin ajustar todavía).
    const montoBorrador = editing ? parseMonto(draft) : null;
    const pctMostrado = montoBorrador !== null ? pctPagoInicial(montoBorrador, vehicleTotal) : pctLabel;

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            aplicarEdicion();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelarEdicion();
        }
    };

    return (
        <div className='SliderBlock'>
            <div className='SliderHeader'>
                <span className='SliderLabel'>Enganche</span>
                {totalIsReady ? (
                    <span className='SliderValue EngancheValue'>
                        <span>{`${pctMostrado}% · `}</span>
                        {editing ? (
                            <span className='EngancheInputWrap'>
                                <span aria-hidden='true'>$</span>
                                <input
                                    ref={inputRef}
                                    className='EngancheInput'
                                    type='text'
                                    inputMode='decimal'
                                    aria-label='Monto del enganche'
                                    value={draft}
                                    onChange={event => setDraft(event.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onBlur={aplicarEdicion}
                                />
                            </span>
                        ) : (
                            <span className={`EngancheAmount${aviso ? ' EngancheAmountError' : ''}`}>
                                {formatMoney(enganche)}
                            </span>
                        )}
                    </span>
                ) : (
                    <span className='SliderValue'>Completa el valor del vehículo</span>
                )}
            </div>

            {aviso && (
                <div className='EngancheAviso' role='status'>
                    <i className='pi pi-exclamation-triangle' />
                    {aviso.texto}
                </div>
            )}

            {totalIsReady ? (
                <div className="SliderContainer">
                    <Slider
                        min={minPct}
                        max={maxPct}
                        step={1}
                        value={sliderPct}
                        className="custom-slider mt-3"
                        tooltip={false}
                        name="down_payment_percentage"
                        onChange={onDownPaymentChange}
                    />
                </div>
            ) : (
                <div className="Empty">
                    Completa el precio del vehículo para ajustar el enganche.
                </div>
            )}

            {puedeEditar && (editing ? (
                <span className='EngancheHint'>Enter para aplicar · Esc para cancelar</span>
            ) : (
                <button type='button' className='EngancheLink' onClick={empezarEdicion}>
                    Escribir monto
                </button>
            ))}
        </div>
    );
}

function FinancingOptionsCard({
    currentTerm,
    plazoVal,
    paymentConfigurations,
    currentDownPaymentPercentage,
    downPaymentPctLabel,
    downPaymentMin,
    downPaymentMax,
    totalIsReady,
    enganche,
    vehicleTotal,
    residualValue,
    residualOptions,
    typeCoverageId,
    residualPercentage,
    setClass,
    formatMoney,
    onPlazoChange,
    onDownPaymentChange,
    onDownPaymentAmountChange,
    onResidualChange,
}) {
    return (
        <section className='FormCard'>
            <div className='SectionHeader'>
                <div>
                    <h2>Opciones de financiamiento</h2>
                </div>
            </div>

            <div className='SliderStack'>
                <div className={`SliderRow${residualValue && typeCoverageId === 2 ? '' : ' SliderRowSingle'}`}>
                    <div className={`SliderBlock${residualValue && typeCoverageId === 2 ? ' SliderBlockCompact' : ''}`}>
                        <div className='SliderHeader'>
                            <span className='SliderLabel'>Plazo</span>
                            <span className='SliderValue'>{currentTerm ? `${currentTerm} meses` : 'Selecciona'}</span>
                        </div>
                        <div className="SliderContainer">
                            <Slider
                                min={parseInt(paymentConfigurations[0]?.configuration)}
                                max={parseInt([...paymentConfigurations].pop()?.configuration)}
                                step={12}
                                value={plazoVal}
                                className="custom-slider mt-3"
                                graduated
                                name="plazo_select"
                                tooltip={false}
                                onChange={onPlazoChange}
                                renderMark={mark => mark}
                            />
                        </div>
                    </div>

                    {residualValue && typeCoverageId === 2 && (
                        <><div className='SliderDivider' /><div className='SliderBlock SliderBlockCompact'>
                            <div className='SliderHeader'>
                                <span className='SliderLabel'>Residual</span>
                                <span className='SliderValue'>
                                    {residualPercentage ? `${parseInt(residualPercentage)}%` : 'Selecciona'}
                                </span>
                            </div>
                            <div className="SliderContainer">
                                <Slider
                                    min={parseInt(residualOptions[0]?.percentage)}
                                    max={parseInt(residualOptions[residualOptions.length - 1]?.percentage)}
                                    step={5}
                                    name="residualValue"
                                    value={parseInt(residualPercentage) || 5}
                                    className={setClass('residualValue') ? 'SliderError' : 'custom-slider mt-3'}
                                    tooltip={false}
                                    graduated
                                    onChange={onResidualChange}
                                    renderMark={mark => `${mark}%`}
                                />
                            </div>
                        </div></>
                    )}
                </div>

                <EngancheBlock
                    enganche={enganche}
                    vehicleTotal={vehicleTotal}
                    totalIsReady={totalIsReady}
                    sliderPct={currentDownPaymentPercentage}
                    pctLabel={downPaymentPctLabel ?? currentDownPaymentPercentage}
                    minPct={downPaymentMin}
                    maxPct={downPaymentMax}
                    formatMoney={formatMoney}
                    onDownPaymentChange={onDownPaymentChange}
                    onDownPaymentAmountChange={onDownPaymentAmountChange}
                />
            </div>
        </section>
    );
}

export { FinancingOptionsCard };
